import { Box, Flex, Loader, Text, Transition } from '@mantine/core';
import { useEffect } from 'react';
import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { useAppContainerContext } from '../AppContainerContext';
import { ChatMode, useChatMessages } from '../chat/hooks/useChatMessages';
import { useChatScroll } from '../chat/hooks/useChatScroll';
import { SiestaAction, SiestaActionType } from '../types';
import { useAppContainer } from '../useAppContainer';
import { AIActionSet } from './AIActionSet';
import { AskSiestaInput } from './AskSiestaInput';
import { ChatEmptyState } from './ChatEmptyState';
import { ChatScrollBlur } from './ChatScrollBlur';
import { CtaAdditionalAI } from './CtaAdditionalAI';
import classes from './SiestaBar.module.css';
import { SiestaBusyGlow } from './SiestaBusyGlow';
import { SiestaBarLayout } from './SiestaBarLayout';
import { SiestaBarMessages } from './SiestaBarMessages';
import { useChatEmptyMessage } from './useChatEmptyMessage';
import { useRecordEditAction } from './useRecordEditAction';
import { useSuggestedActions } from './useSuggestedActions';

export function SiestaBar() {
  const { siestaMode, setSiestaMode, siestaBusyGlowTop } = useAppContainer();
  const { contentMeta, conversationId } = useAppContainerContext();
  const emptyMessage = useChatEmptyMessage();
  const { clientId } = useCurrentClientIfAvailable();
  const { recordAction } = useRecordEditAction();

  const chatMode = contentMeta?.chatMode;
  const isDocumentMode = chatMode === ChatMode.DOCUMENT;
  const getDocumentContent = contentMeta?.getDocumentContent;

  const requestBodyBuilder = (message: string) => ({
    conversationId,
    documentContent: getDocumentContent?.() ?? contentMeta?.metadata?.documentContent ?? '',
    editInstruction: message,
    clientId,
  });

  const chatOptions = isDocumentMode
    ? {
        conversationId,
        mode: ChatMode.DOCUMENT,
        entityId: contentMeta?.entityId ?? '',
        onStateChange: contentMeta?.onChatStateChange,
        requestBodyBuilder,
      }
    : {
        conversationId,
        mode: ChatMode.CLIENT,
        onStateChange: contentMeta?.onChatStateChange,
      };

  const {
    messages,
    sendUserMessage,
    clearMessages,
    currentChatState,
    cancelStreaming,
    latestIncomingId,
  } = useChatMessages(chatOptions);

  const isProcessing = currentChatState !== null;

  useEffect(() => {
    if (siestaMode !== 'chat') {
      cancelStreaming();
    }
  }, [siestaMode, cancelStreaming]);
  const lastMessage = messages[messages.length - 1];
  const contentSignal = lastMessage?.answerContent.length ?? 0;
  const { scrollRef, handleScroll, forceScrollToBottom } = useChatScroll(
    messages.length,
    contentSignal,
  );

  const { actions: siestaActions, isLoading: isLoadingActions } = useSuggestedActions({
    documentId: contentMeta?.entityId,
    documentContent: contentMeta?.metadata?.documentContent,
    clientId,
    conversationId,
  });

  const canFetchActions = !!(
    contentMeta?.entityId &&
    contentMeta?.metadata?.documentContent &&
    clientId
  );

  const handleActionClick = async (action: SiestaAction) => {
    if (action.type !== SiestaActionType.CHAT) return;

    setSiestaMode('chat');
    if (clientId) {
      await sendUserMessage(action.prompt ?? action.label, clientId);
      forceScrollToBottom();
    }

    if (contentMeta?.entityId && clientId) {
      recordAction({
        documentId: contentMeta.entityId,
        actionText: action.label,
        source: 'SUGGESTED',
        conversationId,
        clientId,
      });
    }
  };

  const handleInputSubmit = async (message: string) => {
    setSiestaMode('chat');
    if (clientId) {
      await sendUserMessage(message, clientId);
      forceScrollToBottom();
    }
  };

  const handleInputFocus = () => {
    setSiestaMode('chat');
  };

  const handleBackToActions = () => {
    clearMessages();
    setSiestaMode('action');
  };

  return (
    <Box pos="relative" h="100%">
      <Transition mounted={siestaMode === 'busy'} transition="fade" duration={200} keepMounted>
        {styles => (
          <Box
            className={classes.busyContainer}
            px="xxxl"
            pos="absolute"
            top="xl"
            style={{ ...styles, overflow: 'hidden', inset: 0 }}
          >
            <SiestaBusyGlow top={siestaBusyGlowTop ?? '50%'} />
            <Text size="textXl" fw={FONT_WEIGHTS.semibold} c="#333" className={classes.busyText}>
              {'Siesta is busy,\nfinish the action to turn it back on'}
            </Text>
          </Box>
        )}
      </Transition>

      <Transition mounted={siestaMode === 'chat'} transition="fade" duration={200}>
        {styles => (
          <Flex
            direction="column"
            h="100%"
            pos="absolute"
            bg="white"
            px="xl"
            pb="xxxl"
            style={{ ...styles, inset: 0 }}
          >
            <CtaAdditionalAI onClick={handleBackToActions} disabled={isProcessing} />
            <ChatScrollBlur />

            <Box
              ref={scrollRef}
              onScroll={handleScroll}
              flex={1}
              pt="xxxxl"
              style={{ overflow: 'auto' }}
            >
              {messages.length === 0 ? (
                <ChatEmptyState title={emptyMessage.title} subtitle={emptyMessage.subtitle} />
              ) : (
                <SiestaBarMessages
                  messages={messages}
                  latestIncomingId={latestIncomingId ?? undefined}
                  isStreaming={isProcessing}
                />
              )}
            </Box>

            <AskSiestaInput
              variant="brand"
              onSubmit={handleInputSubmit}
              autoFocus
              submitDisabled={isProcessing}
            />
          </Flex>
        )}
      </Transition>

      <Transition mounted={siestaMode === 'action'} transition="fade" duration={200} keepMounted>
        {styles => (
          <Box pos="absolute" style={{ ...styles, inset: 0 }}>
            <SiestaBarLayout>
              <Flex h="100%" direction="column" justify="space-between">
                <Flex flex={1} align="center" justify="center">
                  {isLoadingActions && canFetchActions ? (
                    <Loader color="white" />
                  ) : (
                    <AIActionSet actions={siestaActions} onActionClick={handleActionClick} />
                  )}
                </Flex>
                {clientId && (
                  <AskSiestaInput
                    variant="light"
                    onSubmit={handleInputSubmit}
                    onFocus={handleInputFocus}
                  />
                )}
              </Flex>
            </SiestaBarLayout>
          </Box>
        )}
      </Transition>
    </Box>
  );
}
